import { deleteApi, getApi } from 'apis';
import { SideDrawerWrapper } from 'components/Global';
import TableActionHeader from 'components/Global/TableActionHeader';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toastText } from 'utils/utils';
import AddCategoryBody from './AddSubCategoryBody';
import CustomTable from './CustomTable';

const STORAGE_KEY = 'grocerySubCategories.selectedCategoryId';
const CATEGORY_FETCH_LIMIT = 1000;

type GroceryCategoryRow = {
	id: string;
	name: string;
	subCategories?: { id: string; name: string }[];
};

const GrocerySubCategoriesTable = () => {
	const [drawerAnimation, setDrawerAnimation] = useState<boolean>(false);
	const [isSideDrawerOpen, setSideDrawerOpen] = useState<boolean>(false);
	const [search] = useState('');
	const [sort] = useState('');
	const [type] = useState('asc');
	const [drawerInfo, setDrawerInfo] = useState({
		drawerTitle: 'Grocery Sub Categories',
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editSelectedSubCategory, setEditSelectedSubCategory] = useState<any>();
	const [categoryData, setCategoryData] = useState<GroceryCategoryRow[]>([]);
	const [selectedCategoryDetail, setSelectedCategoryDetail] =
		useState<GroceryCategoryRow | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
	const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
		() => sessionStorage.getItem(STORAGE_KEY) ?? undefined,
	);

	const handleCategoryChange = useCallback((categoryId: string | undefined) => {
		setSelectedCategory(categoryId);
		setSelectedCategoryDetail(null);
		if (categoryId) {
			sessionStorage.setItem(STORAGE_KEY, categoryId);
		} else {
			sessionStorage.removeItem(STORAGE_KEY);
		}
	}, []);

	const reconcileSelection = useCallback(
		async (categories: GroceryCategoryRow[]) => {
			if (!categories.length) {
				handleCategoryChange(undefined);
				setSelectedCategoryDetail(null);
				return;
			}

			const preferredId = sessionStorage.getItem(STORAGE_KEY) ?? undefined;

			if (
				preferredId &&
				categories.some((category) => category.id === preferredId)
			) {
				handleCategoryChange(preferredId);
				setSelectedCategoryDetail(null);
				return;
			}

			if (preferredId) {
				try {
					const res = await getApi(
						`/grocery-category/get-category/${preferredId}`,
					);
					const detail = res?.data?.data as GroceryCategoryRow | undefined;
					if (detail?.id) {
						handleCategoryChange(detail.id);
						setSelectedCategoryDetail(detail);
						return;
					}
				} catch {
					// fall through to first category
				}
			}

			const firstId = categories[0]?.id;
			if (firstId) {
				handleCategoryChange(firstId);
				setSelectedCategoryDetail(null);
			}
		},
		[handleCategoryChange],
	);

	const removeDrawerFromDom = () => {
		setSideDrawerOpen(false);
	};

	const openDrawerHandler = () => {
		setDrawerInfo({ drawerTitle: 'Add Sub Category' });
		setDrawerAnimation(true);
		setSideDrawerOpen(true);
	};

	const closeDrawerByAnimation = () => {
		setEditSelectedSubCategory(undefined);
		setDrawerAnimation(false);
	};

	const showModal = () => {
		setIsModalOpen(true);
	};

	const setDrawerInfoHandler = (drawerTitle: string) => {
		setDrawerInfo({ drawerTitle });
	};

	const handleOk = () => {
		setIsModalOpen(false);
		setEditSelectedSubCategory(null);
	};

	const handleCancel = () => {
		setIsModalOpen(false);
		setEditSelectedSubCategory(null);
	};

	const deleteHandler = async () => {
		if (editSelectedSubCategory) {
			try {
				setIsDeleteLoading(true);
				const resp = await deleteApi(
					`/grocery-sub-category/delete/${editSelectedSubCategory?.id}`,
				);
				toastText(
					resp?.data?.message ?? 'Sub Category deleted successfully',
					'success',
				);
			} catch (error: any) {
				toastText(
					error.response?.data?.message ??
						'Something went wrong in delete category',
					'error',
				);
			} finally {
				setIsDeleteLoading(false);
				handleCancel();
				fetchCategory();
			}
		}
	};

	const fetchCategory = useCallback(async () => {
		setIsLoading(true);
		const query: Record<string, string | number> = {
			limit: CATEGORY_FETCH_LIMIT,
			page: 1,
		};
		if (search) query.search = search;
		if (sort) {
			query.sort = sort;
			query.type = type;
		}

		try {
			const res = await getApi('/grocery-category/get-all', query);
			const categories = Array.isArray(res?.data?.data)
				? (res.data.data as GroceryCategoryRow[])
				: [];
			setCategoryData(categories);
			await reconcileSelection(categories);
		} catch (error: any) {
			toastText(error?.response?.data?.error?.errorDescription, 'error');
		} finally {
			setIsLoading(false);
		}
	}, [search, type, sort, reconcileSelection]);

	useEffect(() => {
		fetchCategory();
	}, [fetchCategory]);

	const activeCategory = useMemo(() => {
		if (!selectedCategory) return null;
		return (
			categoryData.find((item) => item.id === selectedCategory) ??
			(selectedCategoryDetail?.id === selectedCategory
				? selectedCategoryDetail
				: null)
		);
	}, [categoryData, selectedCategory, selectedCategoryDetail]);

	const subCategoryRows = activeCategory?.subCategories ?? [];

	const emptyTableMessage = !selectedCategory
		? 'Select a category to view subcategories'
		: 'No subcategories for this category';

	return (
		<>
			<div className={''}>
				<TableActionHeader></TableActionHeader>

				<CustomTable
					dataSource={categoryData}
					subCategoryRows={subCategoryRows}
					emptyTableMessage={emptyTableMessage}
					isLoading={isLoading}
					openDrawerHandler={openDrawerHandler}
					setDrawerInfoHandler={setDrawerInfoHandler}
					setEditSelectedSubCategory={setEditSelectedSubCategory}
					showModal={showModal}
					selectedCategory={selectedCategory}
					setSelectedCategory={handleCategoryChange}
				/>
			</div>
			<ConfirmDelete
				handleCancel={handleCancel}
				handleOk={handleOk}
				isModalOpen={isModalOpen}
				deleteHandler={deleteHandler}
				isLoading={isDeleteLoading}
				selectedDeleteData={editSelectedSubCategory?.name}
				deleteHeading="Sub Category"
			/>
			{isSideDrawerOpen && (
				<SideDrawerWrapper
					isOpen={drawerAnimation}
					removeDrawerFromDom={removeDrawerFromDom}
					closeDrawerByAnimation={closeDrawerByAnimation}
					headerTitle={drawerInfo.drawerTitle}
					position="right"
					width="half"
				>
					<AddCategoryBody
						closeDrawerByAnimation={closeDrawerByAnimation}
						editSelectedSubCategory={editSelectedSubCategory}
						setEditSelectedSubCategory={setEditSelectedSubCategory}
						fetchCategory={fetchCategory}
						selectedCategory={selectedCategory}
					/>
				</SideDrawerWrapper>
			)}
		</>
	);
};

export default GrocerySubCategoriesTable;
